import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ImageIcon, Plus, Search } from 'lucide-react'

import type { ShopGroupFilter, ShopGroupRow } from '@/lib/api/shop-catalog-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useSetShopGroupVisibility,
    useShopCategories,
    useShopGroups,
} from '@/hooks/use-shop-catalog-query'
import { m } from '@/lib/paraglide/messages'

// Bán lẻ › Nhóm sản phẩm: nhóm = trang sản phẩm trên web / Mini App, gồm
// nhiều loại (SKU ERP). Bật/tắt hiện web / Mini App ngay trên dòng.
export const Route = createFileRoute('/_app/_authed/_sidebar/retail/groups/')({
    component: GroupsPage,
})

const ALL = 'all'

/** "true" | "false" | ALL → bool | undefined cho bộ lọc. */
export function triState(v: string): boolean | undefined {
    return v === ALL ? undefined : v === 'true'
}

export function GroupsPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shop_catalog.write')
    const [q, setQ] = useState('')
    const [category, setCategory] = useState(ALL)
    const [web, setWeb] = useState(ALL)
    const [mini, setMini] = useState(ALL)
    const [inStock, setInStock] = useState(false)
    const [bestSeller, setBestSeller] = useState(false)
    const filter: ShopGroupFilter = {
        q: q.trim() || undefined,
        category_id: category === ALL ? undefined : category,
        show_web: triState(web),
        show_miniapp: triState(mini),
        in_stock: inStock || undefined,
        best_seller: bestSeller || undefined,
    }
    const query = useShopGroups(filter)
    const all = useShopGroups({})
    const categories = useShopCategories().data?.data ?? []
    const visibility = useSetShopGroupVisibility()
    const rows = query.data?.data ?? []
    const total = all.data?.data?.length ?? 0
    const colCount = canWrite ? 8 : 7

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.shop_group_title()}
                subtitle={m.shop_group_subtitle()}
                primaryActions={
                    canWrite ? (
                        <Button size="sm" asChild>
                            <Link to="/retail/groups/new">
                                <Plus className="mr-1 size-4" />
                                {m.shop_group_add()}
                            </Link>
                        </Button>
                    ) : undefined
                }
            />
            <div className="flex flex-wrap items-end gap-3">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="pl-8"
                        value={q}
                        aria-label={m.shop_group_search()}
                        placeholder={m.shop_group_search()}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
                <FilterSelect
                    id="flt-cat"
                    label={m.shop_group_filter_category()}
                    value={category}
                    onChange={setCategory}
                    options={categories.map((c) => ({
                        value: c.id,
                        label: c.name,
                    }))}
                />
                <FilterSelect
                    id="flt-web"
                    label={m.shop_show_web()}
                    value={web}
                    onChange={setWeb}
                    options={yesNo()}
                />
                <FilterSelect
                    id="flt-mini"
                    label={m.shop_show_miniapp()}
                    value={mini}
                    onChange={setMini}
                    options={yesNo()}
                />
                <Button
                    type="button"
                    size="sm"
                    variant={inStock ? 'default' : 'outline'}
                    aria-pressed={inStock}
                    onClick={() => setInStock(!inStock)}
                >
                    {m.shop_in_stock()}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant={bestSeller ? 'default' : 'outline'}
                    aria-pressed={bestSeller}
                    onClick={() => setBestSeller(!bestSeller)}
                >
                    {m.shop_best_seller()}
                </Button>
            </div>
            <p className="text-sm text-muted-foreground">
                {m.shop_group_count({
                    shown: String(rows.length),
                    total: String(total),
                })}
            </p>
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.shop_group_name_label()}</TableHead>
                            <TableHead>{m.shop_group_categories()}</TableHead>
                            <TableHead className="text-right">
                                {m.shop_group_variant_count()}
                            </TableHead>
                            <TableHead>{m.shop_in_stock()}</TableHead>
                            <TableHead className="text-center">
                                {m.shop_show_web()}
                            </TableHead>
                            <TableHead className="text-center">
                                {m.shop_show_miniapp()}
                            </TableHead>
                            <TableHead className="text-center">
                                {m.shop_best_seller()}
                            </TableHead>
                            {canWrite && <TableHead className="w-16" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {query.isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={colCount}
                                    className="text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : query.isError ? (
                            <TableRow>
                                <TableCell
                                    colSpan={colCount}
                                    className="text-destructive"
                                >
                                    {m.shop_group_load_failed()}
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={colCount}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.shop_group_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((g) => (
                                <GroupRow
                                    key={g.id}
                                    group={g}
                                    canWrite={canWrite}
                                    onToggle={(v) =>
                                        visibility.mutate({ id: g.id, ...v })
                                    }
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
            <p className="text-sm italic text-muted-foreground">
                {m.shop_group_list_note()}
            </p>
        </div>
    )
}

function yesNo() {
    return [
        { value: 'true', label: m.shop_yes() },
        { value: 'false', label: m.shop_no() },
    ]
}

function FilterSelect({
    id,
    label,
    value,
    onChange,
    options,
}: {
    id: string
    label: string
    value: string
    onChange: (v: string) => void
    options: { value: string; label: string }[]
}) {
    return (
        <div className="space-y-1">
            <Label htmlFor={id} className="text-xs text-muted-foreground">
                {label}
            </Label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger id={id} className="w-40">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={ALL}>{m.shop_all()}</SelectItem>
                    {options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    )
}

function GroupRow({
    group: g,
    canWrite,
    onToggle,
}: {
    group: ShopGroupRow
    canWrite: boolean
    onToggle: (v: { show_web?: boolean; show_miniapp?: boolean }) => void
}) {
    return (
        <TableRow>
            <TableCell>
                <div className="flex items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
                        {g.cover_thumb_url ? (
                            <img
                                src={g.cover_thumb_url}
                                alt=""
                                className="size-full object-cover"
                            />
                        ) : (
                            <ImageIcon className="size-4 text-muted-foreground" />
                        )}
                    </div>
                    <Link
                        to="/retail/groups/$groupId"
                        params={{ groupId: g.id }}
                        className="font-medium text-primary hover:underline"
                    >
                        {g.name}
                    </Link>
                </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
                {g.categories.map((c) => c.name).join(', ') || '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {g.variant_count}
            </TableCell>
            <TableCell>
                {g.variant_count === 0
                    ? m.shop_group_hidden_no_variants()
                    : g.in_stock_variants === 0
                      ? m.shop_group_out_of_stock()
                      : m.shop_group_in_stock_count({
                            n: String(g.in_stock_variants),
                            total: String(g.variant_count),
                        })}
            </TableCell>
            <TableCell className="text-center">
                <Switch
                    checked={g.show_web}
                    disabled={!canWrite}
                    aria-label={m.shop_toggle_web_aria({ name: g.name })}
                    onCheckedChange={(v) => onToggle({ show_web: v })}
                />
            </TableCell>
            <TableCell className="text-center">
                <Switch
                    checked={g.show_miniapp}
                    disabled={!canWrite}
                    aria-label={m.shop_toggle_miniapp_aria({ name: g.name })}
                    onCheckedChange={(v) => onToggle({ show_miniapp: v })}
                />
            </TableCell>
            <TableCell className="text-center tabular-nums">
                {g.best_seller_rank ? `#${g.best_seller_rank}` : '—'}
            </TableCell>
            {canWrite && (
                <TableCell>
                    <Button size="sm" variant="ghost" asChild>
                        <Link
                            to="/retail/groups/$groupId"
                            params={{ groupId: g.id }}
                        >
                            {m.common_edit()}
                        </Link>
                    </Button>
                </TableCell>
            )}
        </TableRow>
    )
}
